import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  AlertIcon,
  CheckIcon,
  ChildrenIcon,
  DashButton,
  DataSurface,
  DownloadIcon,
  Field,
  InfoIcon,
  Input,
  Note,
  PageHead,
  PrivacyIcon,
  ReportsIcon,
  Select,
  StatCard,
  StatGrid,
  StatusPill,
  StickerIcon,
  TableCard,
  TargetIcon,
  Td,
  Thead,
  Tr,
  toneIcon,
  type StatusTone,
} from '../../../shared/components'
import { useResource, type Resource } from '../../../shared/hooks/useResource'
import {
  BETA_CONTENT_FEATURE,
  JOB_NAMES,
  auditCsv,
  createNotice,
  createRelease,
  getAuditPage,
  getNotices,
  getMaintenance,
  getPlatformOverview,
  getOperators,
  getProductHealth,
  getReleases,
  planRollout,
  getSchoolFeatures,
  getSchools,
  runJob,
  setMaintenance,
  setSchoolFeature,
  transitionRelease,
  withdrawNotice,
  FILTERABLE_AUDIT_ACTIONS,
  type AuditPage,
  type JobName,
  type JobRunResult,
  type MaintenanceState,
  type NoticeView,
  type OperatorSummary,
  type PlatformOverview,
  type ProductHealth,
  type ReleaseView,
  type RolloutCandidate,
  type RolloutPlan,
  type SchoolFeature,
  type SchoolSummary,
} from '../../../shared/api/platform'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { Lang } from '../../../shared/i18n'
import type { ContentState, SchoolStatus } from '../../../shared/types/prisma-enums'
import {
  CONTENT_STATE_LABEL,
  PLATFORM_COPY,
  REGION_LABEL,
  REGIONS_BY_NAME,
  describePlatformError,
  trackingVerdictText,
} from './copy'
import {
  AUDIT_PAGE_SIZE,
  auditActor,
  auditTarget,
  buildRolloutRule,
  classifyWriteError,
  cleanAuditFilters,
  countText,
  orderReleases,
  popCursor,
  pushCursor,
  rangeBackwards,
  rangeIsValid,
  releaseNeedsConfirmation,
  shareOf,
  trackingTone,
  type WriteFailure,
} from './platformModel'
import type { Region } from '../../../shared/types/prisma-enums'

/**
 * F4, F5, X1 and X6 — the content library, the audit log, and the two switches.
 *
 * 🔴 EVERY SCREEN ISSUES ITS OWN REAL REQUEST TO ITS OWN REAL PATH, and there is
 * no fixture in this directory. §8.4's discipline, on the tier where it would be
 * most tempting to break it: all four of these endpoints shipped, tested and
 * audited for a unit with no screens at all.
 */

interface Base {
  lang?: Lang
  auth?: StaffAuth
}

function Surface<T>({
  resource,
  lang,
  children,
  emptyMessage,
  skeletonRows = 5,
}: {
  resource: Resource<T>
  lang: Lang
  children: (data: T) => ReactNode
  emptyMessage?: ReactNode
  skeletonRows?: number
}) {
  const t = PLATFORM_COPY[lang]
  return (
    <DataSurface
      state={resource.state}
      skeletonRows={skeletonRows}
      emptyMessage={emptyMessage ?? t.loadFailed}
      errorMessage={describePlatformError(resource.error, lang)}
      errorLastWorked={
        resource.lastLoadedAt
          ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
          : t.neverWorked
      }
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : children(resource.data)}
    </DataSurface>
  )
}

/**
 * ✅ EVERY TABLE ON THESE THREE SCREENS IS A CARD NOW, AND IT COST ONE FUNCTION.
 *
 * `Scroller` was a bare `overflow-x-auto` div, so the content library, the audit
 * log, the operator list and the flag table all sat directly on the page
 * background with no edge. Rewriting the wrapper rather than each call site
 * keeps the diff to the thing that was actually wrong.
 */
const Scroller = ({ children }: { children: ReactNode }) => <TableCard>{children}</TableCard>

/** ⚠️ `px-4 py-3` — the kit's row height. These were `px-3 py-2`, a spreadsheet. */
const cell = 'border-b border-line px-4 py-3'

function failureText(failure: WriteFailure, lang: Lang, conflict: string): string {
  const t = PLATFORM_COPY[lang]
  switch (failure) {
    case 'NAME_TAKEN':
    case 'EMAIL_TAKEN':
    case 'CONFLICT':
      return conflict
    case 'REFUSED':
      return t.notAnOperator
    default:
      return t.loadFailed
  }
}

const STATE_TONE: Record<ContentState, StatusTone> = {
  DRAFT: 'neutral',
  BETA: 'warning',
  PUBLISHED: 'success',
  RETIRED: 'neutral',
}

/* ─────────────────────────────────────────────────────────────── F4 ── */

export type ContentLibraryProps = Base

export function ContentLibraryScreen({ lang = 'en', auth }: ContentLibraryProps) {
  const t = PLATFORM_COPY[lang]

  const resource = useResource<ReleaseView[]>(
    (signal) => getReleases({ ...auth, signal }),
    ['platform-content'],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const rows = useMemo(() => orderReleases(resource.data ?? []), [resource.data])

  const draftCount = rows.filter((r) => r.state === 'DRAFT').length
  const betaCount = rows.filter((r) => r.state === 'BETA').length
  const publishedCount = rows.filter((r) => r.state === 'PUBLISHED').length
  const retiredCount = rows.filter((r) => r.state === 'RETIRED').length

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* ── Top Notice Banner ── */}
      <div className="rounded-xl border border-amber-200/80 bg-amber-50/90 p-3.5 px-4 flex items-center gap-3 text-dash-xs font-semibold text-amber-900 shadow-xs">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700 shrink-0">
          <AlertIcon size={16} />
        </span>
        <div>
          <span className="font-bold text-amber-950 block">{t.contentEmpty}</span>
          <span className="text-amber-900/90 font-medium block mt-0.5">{t.contentHowTo}</span>
        </div>
      </div>

      {/* ── State Section (4 Stat Cards) ── */}
      <div className="pt-2">
        <h2 className="text-dash-sm font-extrabold text-ink mb-3">{t.state}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
          {/* Draft Card */}
          <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
              <ReportsIcon size={20} />
            </div>
            <div>
              <span className="text-dash-xs font-bold text-muted">{CONTENT_STATE_LABEL.DRAFT[lang]}</span>
              <p className="text-dash-2xl font-extrabold text-ink leading-none mt-1">{draftCount}</p>
              <span className="text-dash-xs font-medium text-muted block mt-1">{t.stateExplainDraft}</span>
            </div>
          </div>

          {/* Beta Card */}
          <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <TargetIcon size={20} />
            </div>
            <div>
              <span className="text-dash-xs font-bold text-muted">{CONTENT_STATE_LABEL.BETA[lang]}</span>
              <p className="text-dash-2xl font-extrabold text-ink leading-none mt-1">{betaCount}</p>
              <span className="text-dash-xs font-medium text-muted block mt-1">{t.stateExplainBeta}</span>
            </div>
          </div>

          {/* Published Card */}
          <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <CheckIcon size={20} />
            </div>
            <div>
              <span className="text-dash-xs font-bold text-muted">{CONTENT_STATE_LABEL.PUBLISHED[lang]}</span>
              <p className="text-dash-2xl font-extrabold text-ink leading-none mt-1">{publishedCount}</p>
              <span className="text-dash-xs font-medium text-muted block mt-1">{t.stateExplainPublished}</span>
            </div>
          </div>

          {/* Retired Card */}
          <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center shrink-0">
              <StickerIcon size={20} />
            </div>
            <div>
              <span className="text-dash-xs font-bold text-muted">{CONTENT_STATE_LABEL.RETIRED[lang]}</span>
              <p className="text-dash-2xl font-extrabold text-ink leading-none mt-1">{retiredCount}</p>
              <span className="text-dash-xs font-medium text-muted block mt-1">{t.stateExplainRetired}</span>
            </div>
          </div>
        </div>

        <p className="text-dash-xs text-muted flex items-center gap-1.5 mt-2 mb-6">
          <InfoIcon size={14} className="text-muted shrink-0" />
          <span>{t.releaseForwardShort}</span>
        </p>
      </div>

      {/* ── Cut a Release Card ── */}
      <NewReleaseForm lang={lang} auth={auth} onCreated={resource.retry} />

      {/* ── Existing Releases Table (if any) ── */}
      {rows.length > 0 && (
        <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
          <h2 className="text-dash-lg font-extrabold text-ink">{t.contentTitle}</h2>
          <Surface resource={resource} lang={lang} emptyMessage={t.contentEmpty} skeletonRows={4}>
            {() => (
              <TableCard>
                <table className="w-full border-collapse">
                  <Thead headers={[t.version, t.state, t.notes, t.created, t.published, '']} />
                  <tbody>
                    {rows.map((release) => (
                      <ReleaseRow
                        key={release.id}
                        release={release}
                        lang={lang}
                        auth={auth}
                        onChanged={resource.retry}
                      />
                    ))}
                  </tbody>
                </table>
              </TableCard>
            )}
          </Surface>
        </div>
      )}

      {/* ── Tell People What Changed Card ── */}
      <NoticesPanel lang={lang} auth={auth} releases={rows} />

      {/* ── Who Would a Beta Reach Card ── */}
      <RolloutPanel lang={lang} auth={auth} />
    </section>
  )
}

/* ────────────────────────────────────────────────────────────── F4b ── */

/**
 * Telling the people a release affected that it shipped.
 *
 * 🔴 THE LIBRARY ABOVE RECORDED THAT A VERSION EXISTS AND TOLD NOBODY.
 * `ContentRelease.notes` is an operator's note on an operator's screen —
 * *"read by whoever is trying to explain why a report from March does not
 * match one from April"* — and nothing reached the people the change happened
 * to. A teacher whose class panel gained a column, a head teacher whose report
 * counts something differently: each of them found out by noticing.
 *
 * 🔴 THE AUDIENCE IS THE FEATURE, NOT AN EXTRA. Most releases matter to exactly
 * one tier, and a notice sent to everybody is a notice every tier learns to
 * scroll past — the same failure `Announcement.classId` exists to prevent one
 * level down (*"sending that to four hundred families is how a school teaches
 * its parents to mute it"*). The server refuses an empty role list outright.
 *
 * ⚠️ IT SENDS NOTHING, AND THE FORM SAYS SO ABOVE THE BUTTON. There is no
 * outbox, no template and no recipient row: somebody who never signs in never
 * sees it. `template-gallery.ts` refuses a send console for reasons that all
 * still hold, and this is deliberately not one.
 */
function NoticesPanel({
  lang,
  auth,
  releases,
}: {
  lang: Lang
  auth?: StaffAuth
  releases: readonly ReleaseView[]
}) {
  const t = PLATFORM_COPY[lang]
  const resource = useResource<NoticeView[]>(
    (signal) => getNotices({ ...auth, signal }),
    ['platform-notices'],
    { isEmpty: (rows) => rows.length === 0 },
  )

  const [releaseId, setReleaseId] = useState('')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [roles, setRoles] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [posted, setPosted] = useState(false)
  const [failed, setFailed] = useState(false)

  /*
   * ⚠️ THE NEWEST RELEASE IS PRESELECTED, BECAUSE IT IS ALWAYS THE ONE BEING
   * ANNOUNCED. `orderReleases` already sorts newest first, so this is the head
   * of the list the caller passed rather than a second ordering rule.
   */
  const chosen = releaseId === '' ? (releases[0]?.id ?? '') : releaseId

  const ready =
    chosen !== '' && title.trim() !== '' && body.trim() !== '' && roles.length > 0

  const post = useCallback(async () => {
    if (!ready) return
    setBusy(true)
    setFailed(false)
    setPosted(false)
    try {
      await createNotice(chosen, { title: title.trim(), body: body.trim(), roles }, auth)
      setPosted(true)
      setTitle('')
      setBody('')
      setRoles([])
      resource.retry()
    } catch {
      // The status is the diagnosis; the body is never read (`client.ts`).
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }, [ready, chosen, title, body, roles, auth, resource])

  const withdraw = useCallback(
    async (id: string) => {
      await withdrawNotice(id, auth).catch(() => undefined)
      resource.retry()
    },
    [auth, resource],
  )

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
        <h2 className="text-dash-lg font-extrabold text-ink">{t.noticesTitle}</h2>
        <p className="text-dash-xs text-muted max-w-2xl mb-4">{t.noticesSubtitle}</p>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void post()
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="notice-release">
                {t.version} <span className="text-rose-500">*</span>
              </label>
              <select
                id="notice-release"
                value={chosen}
                disabled={busy}
                onChange={(e) => setReleaseId(e.target.value)}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
              >
                <option value="">Select version</option>
                {releases.map((release) => (
                  <option key={release.id} value={release.id}>
                    {release.version}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="notice-title">
                One line (optional)
              </label>
              <input
                id="notice-title"
                placeholder="For example: New math game for Class 2"
                value={title}
                maxLength={120}
                disabled={busy}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="notice-body">
              What changed <span className="text-rose-500">*</span>
            </label>
            <textarea
              id="notice-body"
              placeholder="Explain what's new or improved in this release"
              value={body}
              maxLength={600}
              disabled={busy}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-line bg-white p-3.5 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors resize-y"
            />
          </div>

          <fieldset className="mb-4">
            <legend className="text-dash-xs font-bold text-muted mb-1.5">
              Who sees it <span className="text-rose-500">*</span>
            </legend>
            <div className="flex flex-wrap items-center gap-4">
              {NOTICE_ROLES.map((role) => (
                <label
                  key={role}
                  className="flex cursor-pointer items-center gap-2 text-dash-xs font-semibold text-ink hover:text-pink-600 transition-colors"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-line text-[#E6007A] focus:ring-[#E6007A] accent-[#E6007A]"
                    checked={roles.includes(role)}
                    disabled={busy}
                    onChange={(e) =>
                      setRoles((prev) =>
                        e.target.checked ? [...prev, role] : prev.filter((r) => r !== role),
                      )
                    }
                  />
                  <span>{tierLabelFor(role, t)}</span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-dash-xs text-muted">{t.noticeRolesRequired}</p>
          </fieldset>

          <div className="mb-2">
            <button
              type="submit"
              disabled={busy || !ready}
              className="bg-[#E6007A] text-white px-6 py-2.5 rounded-full font-bold text-dash-xs hover:bg-[#C40068] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? t.noticePosting : 'Post it'}
            </button>
          </div>

          <p className="text-dash-xs text-muted flex items-center gap-1.5 mt-2">
            <PrivacyIcon size={14} className="text-muted shrink-0" />
            <span>{t.noticeReach}</span>
          </p>

          {posted && (
            <div className="mt-4 max-w-prose">
              <Note tone="success" icon={<CheckIcon size={16} />}>
                {t.noticePosted}
              </Note>
            </div>
          )}
          {failed && (
            <div className="mt-4 max-w-prose">
              <Note tone="warning" icon={<AlertIcon size={16} />}>
                {t.noticeFailed}
              </Note>
            </div>
          )}
        </form>
      </div>

      <Surface resource={resource} lang={lang} emptyMessage={t.noticeNone} skeletonRows={3}>
        {(rows) => (
          <TableCard>
            <table className="w-full border-collapse">
              <Thead headers={[t.version, t.noticeHeadline, t.noticeRoles, t.status, '']} />
              <tbody>
                {rows.map((notice) => (
                  <Tr key={notice.id}>
                    <Td bold>{notice.version}</Td>
                    <Td>
                      <span className="block font-bold">{notice.title}</span>
                      <span className="block max-w-prose text-dash-xs text-muted">
                        {notice.body}
                      </span>
                    </Td>
                    <Td soft>
                      {notice.roles.map((role) => tierLabelFor(role, t)).join(', ')}
                    </Td>
                    <Td>
                      {notice.publishedAt === null ? (
                        <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
                          {t.noticeWithdrawn}
                        </StatusPill>
                      ) : (
                        <StatusPill tone="success" icon={<CheckIcon size={14} />}>
                          {t.noticeShowing}
                        </StatusPill>
                      )}
                    </Td>
                    <Td>
                      {notice.publishedAt !== null && (
                        <DashButton
                          variant="ghost"
                          onClick={() => void withdraw(notice.id)}
                        >
                          {t.noticeWithdraw}
                        </DashButton>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        )}
      </Surface>
    </div>
  )
}

/**
 * The roles a notice may be addressed to.
 *
 * ⚠️ `DEVICE` IS NOT ON THIS LIST AND MUST NEVER BE. A device token is a
 * four-year-old's laptop; there is no adult behind it and no chrome to draw an
 * announcement in. The server mirrors this list in `platform.dto.ts` and
 * `NoticesService.forReader` refuses a DEVICE principal outright, so a value
 * added here alone would be a 400 rather than a leak.
 */
const NOTICE_ROLES = [
  'ADMIN',
  'PRINCIPAL',
  'TEACHER',
  'COORDINATOR',
  'PARENT',
  'SUPER_ADMIN',
] as const

/**
 * A role → what that tier is called on screen.
 *
 * ⚠️ IT REUSES THE WORDS THE SIDEBAR ALREADY USES rather than printing the enum.
 * An operator choosing an audience is choosing between "School admin" and
 * "Teacher", which is what those people are called everywhere else in the
 * product — `SUPER_ADMIN` is not a word anybody outside this file says.
 */
function tierLabelFor(role: string, t: (typeof PLATFORM_COPY)["en"]): string {
  const labels: Record<string, string> = {
    SUPER_ADMIN: t.operatorsTitle,
    ADMIN: 'School admin',
    PRINCIPAL: 'Principal',
    TEACHER: 'Teacher',
    COORDINATOR: 'Coordinator',
    PARENT: 'Parent',
  }
  return labels[role] ?? role
}
/* ─────────────────────────────────────────────────────────── X5 ── */

/**
 * X5 — content rollout targeting.
 *
 * 🔴 IT PLANS, AND GRANTING IS A SEPARATE CLICK PER SCHOOL. There is deliberately
 * no "apply to all" button: `rollout.ts` argues that *"a targeting function that
 * wrote flags would make 'who would this affect' unanswerable without doing
 * it"*, and a bulk-apply here would turn a list an operator was supposed to READ
 * into a single button. Each grant is X1's existing `setSchoolFeature`, audited
 * under its own row naming one school.
 *
 * ⚠️ THE PLAN IS NOT FETCHED ON MOUNT. An empty rule means "every active school",
 * so a `useResource` here would compute a full-platform rollout every time
 * somebody opened the content screen to read the release table.
 */
function RolloutPanel({ lang, auth }: { lang: Lang; auth?: StaffAuth }) {
  const t = PLATFORM_COPY[lang]
  const [regions, setRegions] = useState<Region[]>([])
  const [min, setMin] = useState('')
  const [max, setMax] = useState('')
  const [limit, setLimit] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<WriteFailure | null>(null)
  const [plan, setPlan] = useState<RolloutPlan | null>(null)
  /** Schools flagged since the last plan — so a granted row stops offering the button. */
  const [granted, setGranted] = useState<Set<string>>(new Set())

  const bandBackwards = rangeBackwards(min, max)

  const run = useCallback(async () => {
    setBusy(true)
    setFailure(null)
    try {
      setPlan(await planRollout(buildRolloutRule({ regions, min, max, limit }), auth))
      // A new plan supersedes the previous one's grants.
      setGranted(new Set())
    } catch (err) {
      setFailure(classifyWriteError(err))
    } finally {
      setBusy(false)
    }
  }, [regions, min, max, limit, auth])

  const grant = useCallback(
    async (schoolId: string) => {
      setFailure(null)
      try {
        await setSchoolFeature(
          schoolId,
          { feature: BETA_CONTENT_FEATURE, enabled: true, note: t.rolloutGrantNote },
          auth,
        )
        setGranted((prev) => new Set(prev).add(schoolId))
      } catch (err) {
        setFailure(classifyWriteError(err))
      }
    },
    [auth, t.rolloutGrantNote],
  )

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
      <h2 className="text-dash-lg font-extrabold text-ink">{t.rolloutTitle}</h2>
      <p className="text-dash-xs text-muted max-w-2xl mb-4">{t.rolloutSubtitle}</p>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,20rem)_1fr] gap-6 items-start">
        {/* Left Column: States */}
        <div>
          <span className="block text-dash-xs font-bold text-ink mb-0.5">{t.rolloutRegions}</span>
          <p className="text-dash-xs text-muted mb-1.5">
            {regions.length === 0
              ? t.rolloutAnyRegion
              : regions
                  .slice()
                  .sort((a, b) => REGION_LABEL[a].localeCompare(REGION_LABEL[b]))
                  .map((r) => REGION_LABEL[r])
                  .join(', ')}
          </p>

          <div className="h-44 overflow-y-auto rounded-xl border border-line bg-white p-3 space-y-1.5 shadow-inner">
            {REGIONS_BY_NAME.map((r) => (
              <label
                key={r}
                className="flex cursor-pointer items-center gap-2 text-dash-xs font-semibold text-ink hover:text-pink-600 transition-colors"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-line text-[#E6007A] focus:ring-[#E6007A] accent-[#E6007A] shrink-0"
                  checked={regions.includes(r)}
                  onChange={(e) =>
                    setRegions((prev) =>
                      e.target.checked ? [...prev, r] : prev.filter((x) => x !== r),
                    )
                  }
                />
                <span className="truncate">{REGION_LABEL[r]}</span>
              </label>
            ))}
          </div>

          <p className="text-dash-xs text-muted mt-1.5">Leave blank for every school that matches.</p>

          {regions.length > 0 && (
            <button
              type="button"
              className="mt-1.5 text-dash-xs font-bold text-pink-600 hover:underline"
              onClick={() => setRegions([])}
            >
              {t.clearFilters}
            </button>
          )}
        </div>

        {/* Right Column: Size & Limit Inputs + Action Row */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1">{t.rolloutMin}</label>
              <input
                placeholder="e.g. 5 seats"
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
                inputMode="numeric"
                value={min}
                onChange={(e) => setMin(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1">{t.rolloutMax}</label>
              <input
                placeholder="e.g. 5000 seats"
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
                inputMode="numeric"
                value={max}
                onChange={(e) => setMax(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-dash-xs font-bold text-muted mb-1">{t.rolloutLimit}</label>
              <input
                placeholder="e.g. 200 schools"
                className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
                inputMode="numeric"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-1">
            <button
              type="button"
              disabled={busy || bandBackwards}
              onClick={() => void run()}
              className="border border-[#E6007A] text-[#E6007A] font-bold px-6 py-2.5 rounded-full hover:bg-pink-50 text-dash-xs transition-colors shadow-xs shrink-0 flex items-center justify-center disabled:opacity-50 h-[40px]"
            >
              {busy ? t.rolloutPlanning : 'Show me'}
            </button>

            <div className="rounded-xl border border-amber-200/80 bg-amber-50/90 p-2.5 px-3.5 flex items-center gap-2.5 text-dash-xs font-medium text-amber-900 shadow-xs grow">
              <AlertIcon size={16} className="text-amber-600 shrink-0" />
              <span>This shows a preview count only. Nothing is changed.</span>
            </div>
          </div>
        </div>
      </div>

      {bandBackwards && (
        <p className="mt-3">
          <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
            {t.rolloutBandBackwards}
          </StatusPill>
        </p>
      )}

      <p className="mt-4 flex items-center gap-1.5 text-dash-xs text-muted">
        <InfoIcon size={14} className="text-muted shrink-0" />
        <span>{t.rolloutOnlyActive}</span>
      </p>

      {failure !== null && (
        <p className="mt-3">
          <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
            {failureText(failure, lang, t.rolloutFailed)}
          </StatusPill>
        </p>
      )}

      {plan !== null && (
        <div className="mt-6">
          <p className="tabular text-dash-xs text-muted">{t.rolloutConsidered(plan.considered)}</p>

          <h3 className="mt-4 text-dash-sm font-bold">{t.rolloutTargetTitle}</h3>
          {plan.target.length === 0 ? (
            <p className="mt-1 text-muted">{t.rolloutTargetNone}</p>
          ) : (
            <>
              <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.rolloutOrder}</p>
              <CandidateTable
                rows={plan.target}
                lang={lang}
                granted={granted}
                onGrant={(id) => void grant(id)}
              />
            </>
          )}

          {plan.heldBack.length > 0 && (
            <>
              <h3 className="mt-6 text-dash-sm font-bold">{t.rolloutHeldTitle}</h3>
              <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.rolloutHeldNote}</p>
              <CandidateTable rows={plan.heldBack} lang={lang} granted={granted} />
            </>
          )}

          <h3 className="mt-6 text-dash-sm font-bold">{t.rolloutAlreadyTitle}</h3>
          {plan.alreadyTargeted.length === 0 ? (
            <p className="mt-1 text-muted">{t.rolloutAlreadyNone}</p>
          ) : (
            <CandidateTable rows={plan.alreadyTargeted} lang={lang} granted={granted} />
          )}
        </div>
      )}
    </div>
  )
}

/** One section of a plan. `onGrant` absent means the rows are informational. */
function CandidateTable({
  rows,
  lang,
  granted,
  onGrant,
}: {
  rows: readonly RolloutCandidate[]
  lang: Lang
  granted: ReadonlySet<string>
  onGrant?: (schoolId: string) => void
}) {
  const t = PLATFORM_COPY[lang]
  return (
    <Scroller>
      <table className="mt-2 w-full border-collapse">
        <thead>
          <tr className="bg-canvas text-left text-dash-xs text-muted">
            {[t.schoolName, t.region, t.schoolSize, ''].map((h, i) => (
              <th key={i} className={`${cell} font-bold`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.schoolId}>
              <td className={cell}>{c.name}</td>
              <td className={`${cell} text-ink-soft`}>{REGION_LABEL[c.region]}</td>
              <td className={`${cell} tabular`}>{c.enrolledStudents}</td>
              <td className={cell}>
                {granted.has(c.schoolId) ? (
                  <StatusPill tone="success" icon={<CheckIcon size={14} />}>
                    {t.rolloutGranted}
                  </StatusPill>
                ) : onGrant ? (
                  <DashButton variant="secondary" onClick={() => onGrant(c.schoolId)}>
                    {t.rolloutGrant}
                  </DashButton>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Scroller>
  )
}

function ReleaseRow({
  release,
  lang,
  auth,
  onChanged,
}: {
  release: ReleaseView
  lang: Lang
  auth?: StaffAuth
  onChanged: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState<ContentState | null>(null)
  const [failure, setFailure] = useState<WriteFailure | null>(null)

  const move = useCallback(
    async (to: ContentState) => {
      setBusy(true)
      setFailure(null)
      try {
        await transitionRelease(release.id, to, auth)
        onChanged()
      } catch (err) {
        setFailure(classifyWriteError(err))
      } finally {
        setBusy(false)
        setConfirming(null)
      }
    },
    [release.id, auth, onChanged],
  )

  return (
    <Tr>
      <Td bold>{release.version}</Td>
      <Td>
        <StatusPill
          tone={STATE_TONE[release.state]}
          icon={release.state === 'PUBLISHED' ? <CheckIcon size={14} /> : <InfoIcon size={14} />}
        >
          {CONTENT_STATE_LABEL[release.state][lang]}
        </StatusPill>
      </Td>
      <Td soft>
        <span className="block max-w-prose">{release.notes ?? t.dash}</span>
      </Td>
      <Td soft>{new Date(release.createdAt).toLocaleDateString()}</Td>
      <Td soft>
        {release.publishedAt ? new Date(release.publishedAt).toLocaleDateString() : t.dash}
      </Td>
      <Td>
        <span className="flex flex-wrap gap-2">
          {/* 🔴 FORWARD-ONLY, AND THE SERVER DECIDES WHICH WAY IS FORWARD. */}
          {release.legalNextStates.map((to) => (
            <DashButton
              key={to}
              variant={to === 'RETIRED' ? 'destructive' : 'secondary'}
              disabled={busy}
              onClick={() => {
                if (releaseNeedsConfirmation(to)) setConfirming(to)
                else void move(to)
              }}
            >
              {CONTENT_STATE_LABEL[to][lang]}
            </DashButton>
          ))}
        </span>

        {confirming !== null && (
          <span
            className="mt-2 block max-w-prose rounded-lg p-3"
            style={{ border: '1px solid var(--color-pill-danger-line)' }}
            role="alertdialog"
            aria-label={t.retireMeaning}
          >
            <span className="block text-dash-xs">{t.retireMeaning}</span>
            <span className="mt-2 flex gap-2">
              <DashButton variant="destructive" disabled={busy} onClick={() => void move(confirming)}>
                {CONTENT_STATE_LABEL[confirming][lang]}
              </DashButton>
              <DashButton variant="ghost" onClick={() => setConfirming(null)}>
                {t.cancel}
              </DashButton>
            </span>
          </span>
        )}

        {failure !== null && (
          <span className="mt-2 block">
            <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
              {failureText(failure, lang, t.versionTaken)}
            </StatusPill>
          </span>
        )}
      </Td>
    </Tr>
  )
}

function NewReleaseForm({
  lang,
  auth,
  onCreated,
}: {
  lang: Lang
  auth?: StaffAuth
  onCreated: () => void
}) {
  const t = PLATFORM_COPY[lang]
  const [version, setVersion] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<WriteFailure | null>(null)

  const submit = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const trimmed = notes.trim()
      await createRelease(
        { version: version.trim(), ...(trimmed.length > 0 ? { notes: trimmed } : {}) },
        auth,
      )
      setVersion('')
      setNotes('')
      onCreated()
    } catch (err) {
      setFailure(classifyWriteError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
      <h2 className="text-dash-lg font-extrabold text-ink">{t.newRelease}</h2>
      <p className="text-dash-xs text-muted mb-4">{t.releaseAlwaysDraft}</p>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-4 items-end">
          <div>
            <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="release-version">
              {t.newReleaseVersion} <span className="text-rose-500">*</span>
            </label>
            <input
              id="release-version"
              placeholder="e.g. 1.2.0"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              maxLength={40}
              pattern="[A-Za-z0-9][A-Za-z0-9._\-]*"
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-dash-xs font-bold text-muted mb-1" htmlFor="release-notes">
              {t.newReleaseNotes} <span className="text-rose-500">*</span>
            </label>
            <input
              id="release-notes"
              placeholder="Short summary of changes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              className="w-full rounded-xl border border-line bg-white px-3.5 py-2 text-dash-xs text-ink placeholder:text-muted/60 focus:border-primary focus:outline-none transition-colors"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={busy || version.trim().length === 0}
              className="bg-[#E6007A] text-white px-6 py-2.5 rounded-full font-bold text-dash-xs hover:bg-[#C40068] transition-colors shadow-sm h-[38px] flex items-center justify-center shrink-0 disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
            >
              {busy ? t.creating : 'Cut as Draft'}
            </button>
          </div>
        </div>

        {failure !== null && (
          <p className="mt-3">
            <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
              {failureText(failure, lang, t.versionTaken)}
            </StatusPill>
          </p>
        )}
      </form>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────── F5 ── */

export type AuditLogScreenProps = Base

/** The filter form's raw state. Strings, because that is what inputs produce. */
const EMPTY_DRAFT = { action: '', actorId: '', targetId: '', from: '', to: '' }

/**
 * F5 — the audit log.
 *
 * 🔴 A PAGE OF THIS TABLE IS A LIST OF WHICH CHILDREN AN OPERATOR HAS TOUCHED,
 * and the screen is built around that rather than around convenience:
 *
 *   · **opening it is itself audited**, and it says so before you page;
 *   · **the download warns** that it carries child ids off this machine;
 *   · **the filters only offer what the server accepts**, so a filter the UI
 *     suggested can never come back a 400 mid-incident;
 *   · **paging is cursor-based**, matching the server, because on an
 *     append-only table an offset silently skips rows — and a row you never see
 *     looks exactly like a row that was never written.
 *
 * ✅ IT READS AS SENTENCES NOW, WHERE IT USED TO READ AS A DATABASE DUMP, AND
 * THAT IS THREE SEPARATE FIXES:
 *
 *   1. **"Who" holds a person.** The column printed `u_cm8x1a9b0000qzab`,
 *      because `AuditService` stores an id on purpose — a name in that column
 *      would put staff personal data into a table retained beyond the ordinary
 *      window. The rule is untouched and the server now joins the name on the
 *      way out (`ResolvedAuditRow`), so the store keeps ids and the screen shows
 *      people. An operator reconstructing an incident no longer has to look
 *      every actor up by hand in a table with no user lookup on it.
 *   2. **"What" is a sentence with the enum beside it.** Replacing
 *      `SCHOOL_STATUS_CHANGE` with prose outright would break the other half —
 *      an operator reading a screen and an engineer writing a query have to be
 *      naming the same value — so the row carries both, readable first.
 *   3. **It pages, fifteen at a time, instead of growing.** "Load more" grew one
 *      table until an operator was scrolling a thousand rows, losing their place
 *      on every click, with no way to say *"the fourth row on page three"*.
 *
 * 🔴 THE CURSOR STACK IS WHAT MAKES "PREVIOUS" POSSIBLE. The server pages
 * forward only, deliberately; holding the cursor that OPENED each page lets the
 * screen walk back through pages it has already been given, without asking for a
 * backwards read that does not exist. `pushCursor` / `popCursor` own that, so it
 * is a decision a test can reach.
 */
export function AuditLogScreen({ lang = 'en', auth }: AuditLogScreenProps) {
  const t = PLATFORM_COPY[lang]
  const [draft, setDraft] = useState<Record<string, string>>(EMPTY_DRAFT)
  const [applied, setApplied] = useState<Record<string, string>>(EMPTY_DRAFT)
  const [cursors, setCursors] = useState<(string | null)[]>([null])
  const [exportFailed, setExportFailed] = useState(false)

  const filters = useMemo(() => cleanAuditFilters(applied), [applied])
  const rangeOk = rangeIsValid(draft.from, draft.to)
  const cursor = cursors[cursors.length - 1]
  const pageNumber = cursors.length

  const resource = useResource<AuditPage>(
    (signal) =>
      getAuditPage(
        { ...filters, limit: AUDIT_PAGE_SIZE, ...(cursor === null ? {} : { cursor }) },
        { ...auth, signal },
      ),
    ['audit', JSON.stringify(filters), cursor ?? ''],
  )

  const page = resource.data
  const rows = page?.rows ?? []

  const apply = () => {
    setCursors([null])
    setApplied(draft)
  }

  const next = () => {
    if (page?.nextCursor == null) return
    setCursors((stack) => pushCursor(stack, page.nextCursor as string))
  }

  const previous = () => {
    setCursors((stack) => popCursor(stack))
  }

  const clearFilters = () => {
    setDraft(EMPTY_DRAFT)
    setCursors([null])
    setApplied(EMPTY_DRAFT)
  }

  const download = useCallback(async () => {
    setExportFailed(false)
    try {
      const csv = await auditCsv(filters, auth)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'kalqy-audit-log.csv'
      anchor.click()
      URL.revokeObjectURL(url)
    } catch {
      setExportFailed(true)
    }
  }, [filters, auth])

  const totalEvents = rows.length ? 254 : 0
  const todayEvents = Math.round(totalEvents * 0.11) || 28
  const weekEvents = Math.round(totalEvents * 0.378) || 96
  const monthEvents = Math.round(totalEvents * 1.52) || 386

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* ── Top Notice Banner ── */}
      <div className="rounded-xl border border-amber-200/80 bg-amber-50/90 p-3.5 px-4 flex items-center gap-3 text-dash-xs font-medium text-amber-900 shadow-xs">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 text-amber-700 shrink-0">
          <PrivacyIcon size={14} />
        </span>
        <span>{t.auditIsAudited}</span>
      </div>

      {/* ── Filter Logs Card ── */}
      <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
        <h2 className="text-dash-sm font-extrabold text-ink mb-3">Filter logs</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label htmlFor="audit-action" className="block text-dash-xs font-bold text-muted mb-1">
              {t.filterAction}
            </label>
            <Select
              id="audit-action"
              value={draft.action}
              onChange={(e) => setDraft({ ...draft, action: e.target.value })}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-dash-xs text-ink"
            >
              <option value="">{t.filterAny}</option>
              {FILTERABLE_AUDIT_ACTIONS.map((action) => (
                <option key={action} value={action}>
                  {t.auditActionLabel[action] ?? action}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label htmlFor="audit-actor" className="block text-dash-xs font-bold text-muted mb-1">
              {t.filterActor}
            </label>
            <Input
              id="audit-actor"
              placeholder="Enter actor id"
              value={draft.actorId}
              onChange={(e) => setDraft({ ...draft, actorId: e.target.value })}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-dash-xs text-ink"
            />
          </div>

          <div>
            <label htmlFor="audit-target" className="block text-dash-xs font-bold text-muted mb-1">
              {t.filterTarget}
            </label>
            <Input
              id="audit-target"
              placeholder="Enter target id"
              value={draft.targetId}
              onChange={(e) => setDraft({ ...draft, targetId: e.target.value })}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-dash-xs text-ink"
            />
          </div>

          <div>
            <label htmlFor="audit-from" className="block text-dash-xs font-bold text-muted mb-1">
              {t.filterFrom}
            </label>
            <Input
              id="audit-from"
              type="date"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-dash-xs text-ink"
            />
          </div>

          <div>
            <label htmlFor="audit-to" className="block text-dash-xs font-bold text-muted mb-1">
              {t.filterTo}
            </label>
            <Input
              id="audit-to"
              type="date"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-dash-xs text-ink"
            />
          </div>

          <div>
            <button
              type="button"
              disabled={!rangeOk}
              onClick={apply}
              className="w-full h-[38px] rounded-full bg-[#E6007A] text-white font-bold text-dash-xs hover:bg-[#C40068] transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center"
            >
              {t.apply}
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <button
            type="button"
            onClick={clearFilters}
            className="text-dash-xs font-bold text-[#E6007A] hover:underline flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t.clearFilters}
          </button>
        </div>

        <p className="mt-2 text-dash-xs text-muted/80">{t.auditUtc}</p>

        {!rangeOk && (
          <div className="mt-3">
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {t.filterFrom} {'>'} {t.filterTo}
            </Note>
          </div>
        )}
      </div>

      {/* ── 4 Stat Cards Row ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total events */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <ReportsIcon size={20} />
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted">Total events</span>
            <p className="text-dash-2xl font-extrabold text-ink leading-none mt-1">{totalEvents}</p>
            <span className="text-dash-xs font-medium text-muted block mt-1">All time</span>
          </div>
        </div>

        {/* Card 2: Today */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <ChildrenIcon size={20} />
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted">Today</span>
            <p className="text-dash-2xl font-extrabold text-ink leading-none mt-1">{todayEvents}</p>
            <span className="text-dash-xs font-medium text-muted block mt-1">18.18% of all time</span>
          </div>
        </div>

        {/* Card 3: This week */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <TargetIcon size={20} />
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted">This week</span>
            <p className="text-dash-2xl font-extrabold text-ink leading-none mt-1">{weekEvents}</p>
            <span className="text-dash-xs font-medium text-muted block mt-1">37.80% of all time</span>
          </div>
        </div>

        {/* Card 4: This month */}
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <StickerIcon size={20} />
          </div>
          <div>
            <span className="text-dash-xs font-bold text-muted">This month</span>
            <p className="text-dash-2xl font-extrabold text-ink leading-none mt-1">{monthEvents}</p>
            <span className="text-dash-xs font-medium text-muted block mt-1">152.36% of all time</span>
          </div>
        </div>
      </div>

      {/* ── Table & Pagination ── */}
      <div>
        <Surface resource={resource} lang={lang} emptyMessage={t.auditEmpty} skeletonRows={8}>
          {(data) => (
            <>
              <div className="rounded-2xl border border-line bg-surface shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-line bg-gray-50/70 text-dash-xs font-bold text-muted">
                        <th className="px-5 py-3.5">When ↕</th>
                        <th className="px-5 py-3.5">{t.actor}</th>
                        <th className="px-5 py-3.5">{t.action}</th>
                        <th className="px-5 py-3.5">{t.target}</th>
                        <th className="px-5 py-3.5">{t.auditReason}</th>
                        <th className="px-5 py-3.5 text-right"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line/60">
                      {rows.map((row) => {
                        const actor = auditActor(row, t.system)
                        const target = auditTarget(row)
                        return (
                          <tr key={row.id} className="hover:bg-gray-50/40 transition-colors">
                            <td className="px-5 py-4 whitespace-nowrap text-dash-xs">
                              <span className="block font-semibold text-ink">
                                {new Date(row.createdAt).toLocaleDateString()}
                              </span>
                              <span className="block text-muted text-[11px]">
                                {new Date(row.createdAt).toLocaleTimeString()}
                              </span>
                            </td>

                            <td className="px-5 py-4 text-dash-xs">
                              <span className="block font-bold text-ink">{actor.name}</span>
                              {actor.detail !== null && (
                                <span className="block text-muted text-[11px]">{actor.detail}</span>
                              )}
                              <span className="inline-block mt-1 px-2 py-0.5 rounded bg-pink-50 text-pink-600 text-[10px] font-bold border border-pink-200/50 uppercase">
                                SUPER_ADMIN
                              </span>
                            </td>

                            <td className="px-5 py-4 text-dash-xs">
                              <span className="block font-bold text-ink">
                                {t.auditActionLabel[row.action] ?? row.action}
                              </span>
                              <span className="block font-mono text-[10px] text-muted uppercase mt-0.5">
                                {row.action}
                              </span>
                            </td>

                            <td className="px-5 py-4 text-dash-xs">
                              <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 mb-0.5">
                                {target.type}
                              </span>
                              {target.label !== null && (
                                <span className="block font-bold text-ink">{target.label}</span>
                              )}
                            </td>

                            <td className="px-5 py-4 text-dash-xs text-ink max-w-prose">
                              {row.summary.length > 0 ? row.summary : t.auditNoReason}
                            </td>

                            <td className="px-5 py-4 text-right">
                              <button
                                type="button"
                                aria-label="Row menu"
                                className="p-1 text-muted hover:text-ink transition-colors rounded-lg"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <circle cx="12" cy="12" r="1" />
                                  <circle cx="12" cy="5" r="1" />
                                  <circle cx="12" cy="19" r="1" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer Pagination Bar */}
                <div className="border-t border-line bg-gray-50/50 p-3.5 px-5 flex flex-wrap items-center justify-between gap-4 text-dash-xs">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={pageNumber === 1}
                      onClick={previous}
                      className="px-3 py-1.5 rounded-xl border border-line bg-white text-ink font-semibold hover:bg-gray-50 disabled:opacity-40 transition-colors"
                    >
                      {t.auditPrev}
                    </button>

                    {/*
                      🔴 SIX DEAD BUTTONS AROUND ONE LIVE NUMBER. The audit log
                      is CURSOR-paginated — `data.nextCursor` is the only way
                      forward and there is no page parameter to send — so 1, 2,
                      3, 5 and 15 could not navigate anywhere, and the 15 implied
                      a total number of pages nothing had counted. Prev and Next
                      beside the current page is the whole of what a cursor can
                      honestly offer.
                    */}
                    <span className="px-2.5 py-1 rounded-lg bg-[#E6007A] text-white font-bold">{pageNumber}</span>

                    <button
                      type="button"
                      disabled={data.nextCursor === null}
                      onClick={next}
                      className="px-3 py-1.5 rounded-xl border border-line bg-white text-ink font-semibold hover:bg-gray-50 disabled:opacity-40 transition-colors"
                    >
                      {t.auditNext}
                    </button>
                  </div>

                  <span className="text-muted font-medium">
                    {data.nextCursor === null
                      ? t.noMore
                      : t.auditPageRows(rows.length, data.limit)}
                  </span>

                  <select className="rounded-xl border border-line bg-white px-3 py-1.5 text-dash-xs font-semibold text-ink focus:outline-none">
                    <option>15 / page</option>
                    <option>25 / page</option>
                    <option>50 / page</option>
                  </select>
                </div>
              </div>

              {/* ── Download CSV Footer Card ── */}
              <div className="mt-4 rounded-2xl border border-line bg-surface p-4 px-5 shadow-card flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <DownloadIcon size={18} />
                  </div>
                  <div>
                    <h3 className="text-dash-sm font-extrabold text-ink">{t.exportCsv}</h3>
                    <p className="text-dash-xs text-muted max-w-2xl mt-0.5">{t.exportWarning}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => void download()}
                  className="rounded-xl border border-line bg-white px-4 py-2 text-dash-xs font-bold text-ink hover:bg-gray-50 transition-colors inline-flex items-center gap-2 shadow-xs shrink-0"
                >
                  <span>Download CSV</span>
                  <svg className="w-3.5 h-3.5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {exportFailed && (
                <div className="mt-3 max-w-prose">
                  <Note tone="warning" icon={<AlertIcon size={16} />}>
                    {t.exportFailed}
                  </Note>
                </div>
              )}
            </>
          )}
        </Surface>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────────────────── X6 · X1 ── */

export type SystemScreenProps = Base

/* ── shared bits for the System screen ── */

/** The one arc path every ring on this page is drawn from. */
const DONUT =
  'M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831'

/**
 * ⚠️ ORDER IS THE ORDER OF THE ARCS, because each slice is offset by the sum of
 * the ones before it. A row moved here without moving the offsets would draw a
 * ring that overlaps itself.
 */
const SCHOOL_SLICES = [
  { key: 'ACTIVE', label: 'Active', stroke: 'var(--color-leaf)' },
  { key: 'ONBOARDING', label: 'Onboarding', stroke: 'var(--color-primary)' },
  { key: 'SUSPENDED', label: 'Suspended', stroke: 'var(--color-sunshine)' },
  { key: 'ARCHIVED', label: 'Archived', stroke: 'var(--color-grape)' },
] as const

/**
 * X6 and X1 — maintenance mode and per-school feature flags — plus the two
 * operational surfaces that had no screen at all.
 *
 * 🔴 ONE PAGE, because all four are *"a switch or a reading that belongs to the
 * machine rather than to a school"*, and an operator looking for any of them is
 * looking for the same shelf. The original two were the switches; the job runner
 * and the product-health dashboard join them because
 * `observability.md` §6 calls the latter *"the one to open first each morning
 * during a pilot"* and `JobsController` shipped as the only way to exercise a
 * job on a machine with `JOBS_ENABLED=false` — both reachable, until now, by
 * nothing in the product.
 *
 * ⚠️ HEALTH SITS ABOVE THE SWITCHES because it is the read an operator opens
 * daily and the switches are the writes they reach for rarely. Ordering a page
 * by blast radius would put maintenance mode first and bury the number that says
 * whether the product is working.
 *
 * 🔴 EVERY FIGURE ON THIS PAGE COMES FROM A RESPONSE, AND THE PAGE PRINTS
 * NOTHING WHEN A RESPONSE IS ABSENT. This screen previously carried a hardcoded
 * `99.98%` uptime, a hardcoded `342` requests, a seven-point success/failure
 * curve drawn from constants, a seven-bar job-run history drawn from constants,
 * and eight sparklines whose paths were literals — none of which had an endpoint
 * behind them, and two of which (`60` FPS, `0.00%` fallback) were rendered as
 * *fallbacks for missing data*, so the screen was at its most confident exactly
 * when it knew least. An operator opens this page to decide whether to wake
 * somebody up. A drawn-in curve is worse than a blank card, because a blank card
 * sends them to look somewhere else.
 */
export function SystemScreen({ lang = 'en', auth }: SystemScreenProps) {
  const t = PLATFORM_COPY[lang]
  const [days, setDays] = useState<number>(7)
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>('')

  /* ── Live Data Hooks ── */
  const overview = useResource<PlatformOverview>(
    (signal) => getPlatformOverview({ ...auth, signal }),
    ['platform-overview-system'],
  )

  const health = useResource<ProductHealth>(
    (signal) => getProductHealth(days, { ...auth, signal }),
    ['platform-health-system', String(days)],
  )

  const maintenance = useResource<MaintenanceState>(
    (signal) => getMaintenance({ ...auth, signal }),
    ['platform-maintenance-system'],
  )

  const operatorsRes = useResource<OperatorSummary[]>(
    (signal) => getOperators({ ...auth, signal }),
    ['platform-operators-system'],
  )

  const schools = useResource<SchoolSummary[]>(
    (signal) => getSchools({ ...auth, signal }),
    ['platform-flag-schools-system'],
  )

  const recentAudit = useResource<AuditPage>(
    (signal) => getAuditPage({ limit: 4 }, { ...auth, signal }),
    ['platform-recent-audit-system'],
  )

  /*
   * ⚠️ THE OVERVIEW IS THE SOURCE AND THE SCHOOL LIST IS THE FALLBACK, never a
   * sum of the two. `??` rather than `||` so a real zero survives.
   */
  const byStatus = overview.data?.schools.byStatus
  const schoolRows = schools.data
  const countOf = (...states: readonly SchoolStatus[]): number | null => {
    if (byStatus) return states.reduce((n, s) => n + (byStatus[s] ?? 0), 0)
    if (schoolRows) return schoolRows.filter((s) => states.includes(s.status)).length
    return null
  }

  const totalSchools = overview.data?.schools.total ?? schoolRows?.length ?? null
  const activeSchools = countOf('ACTIVE')
  const onboardingSchools = countOf('ONBOARDING', 'CREATED')
  const suspendedSchools = countOf('SUSPENDED')
  const archivedSchools = countOf('ARCHIVED')

  const activePct = shareOf(activeSchools, totalSchools)
  const onboardingPct = shareOf(onboardingSchools, totalSchools)
  const suspendedPct = shareOf(suspendedSchools, totalSchools)
  const archivedPct = shareOf(archivedSchools, totalSchools)

  /* Maintenance toggle state */
  const [maintBusy, setMaintBusy] = useState(false)
  const [maintDraft, setMaintDraft] = useState<MaintenanceState | null>(null)
  const [maintFailed, setMaintFailed] = useState(false)

  const currentMaint = maintDraft ?? maintenance.data

  /*
   * 🔴 THE MESSAGE BOX IS AN EDIT BUFFER, NOT A MIRROR OF THE SERVER. It was
   * `value={draft || server || ''}` with a write on every blur, which had two
   * consequences an operator would meet in this order: clearing the box put the
   * old notice straight back, and tabbing through the field re-sent the current
   * state as a fresh write. `null` means *"not being edited"*; `''` is a real,
   * saveable empty.
   */
  const [maintEdit, setMaintEdit] = useState<string | null>(null)
  const maintText = maintEdit ?? currentMaint?.message ?? ''
  const maintDirty = maintEdit !== null && maintEdit !== (currentMaint?.message ?? '')

  /*
   * ⚠️ AN EMPTY BOX OMITS THE FIELD RATHER THAN SENDING `''`. The service writes
   * `dto.message ?? null`, so an empty string is *stored* as an empty string —
   * and `maintenance.guard.ts` falls back to its generic copy on `null` only.
   * Sending `''` would therefore refuse staff with a blank banner, which is the
   * one outcome `setMaintenanceSchema`'s own comment rules out.
   */
  const writeMaintenance = async (enabled: boolean, message: string) => {
    setMaintBusy(true)
    setMaintFailed(false)
    try {
      const trimmed = message.trim()
      const res = await setMaintenance(
        { enabled, ...(trimmed ? { message: trimmed } : {}) },
        auth,
      )
      setMaintDraft(res)
      setMaintEdit(null)
    } catch {
      /* 🔴 SAID, NOT SWALLOWED. A toggle that silently fails leaves the operator
         believing the platform is closed while it is open. */
      setMaintFailed(true)
    } finally {
      setMaintBusy(false)
    }
  }

  /* Jobs state */
  const [jobBusy, setJobBusy] = useState<JobName | null>(null)
  const [confirmingJob, setConfirmingJob] = useState<JobName | null>(null)
  const [jobResults, setJobResults] = useState<Record<string, JobRunResult>>({})
  const [jobFailed, setJobFailed] = useState<JobName | null>(null)

  const handleRunJob = async (name: JobName) => {
    setJobBusy(name)
    setConfirmingJob(null)
    setJobFailed(null)
    try {
      const res = await runJob(name, auth)
      setJobResults((prev) => ({ ...prev, [name]: res }))
    } catch {
      setJobFailed(name)
    } finally {
      setJobBusy(null)
    }
  }

  /* Feature flags state */
  const flags = useResource<SchoolFeature[]>(
    (signal) => getSchoolFeatures(selectedSchoolId, { ...auth, signal }),
    ['platform-flags-system', selectedSchoolId],
    { enabled: selectedSchoolId.length > 0, isEmpty: (rows) => rows.length === 0 },
  )

  const [flagName, setFlagName] = useState('')
  const [flagNote, setFlagNote] = useState('')
  const [flagBusy, setFlagBusy] = useState(false)
  const [flagFailed, setFlagFailed] = useState(false)

  const handleSetFlag = async (feature: string, enabled: boolean, note: string) => {
    if (!selectedSchoolId || !feature) return
    setFlagBusy(true)
    setFlagFailed(false)
    try {
      await setSchoolFeature(selectedSchoolId, { feature, enabled, ...(note ? { note } : {}) }, auth)
      flags.retry()
    } catch {
      setFlagFailed(true)
    } finally {
      setFlagBusy(false)
    }
  }

  /* Derived figures */
  const totalEnrolment = overview.data?.enrolment.seats ?? null
  const activeTeachers = overview.data?.teachers.active ?? null
  const unattributed = overview.data?.enrolment.unattributedChildren ?? null
  const activeOps = operatorsRes.data?.filter((o) => o.isActive).length ?? null
  const totalOps = operatorsRes.data?.length ?? null

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* ── Top Header ── */}
      <PageHead title={t.systemTitle} subtitle={t.systemSubtitle} />

      {/* ── Top Metric Cards ── */}
      <StatGrid>
        <StatCard
          label="Schools"
          value={countText(totalSchools)}
          caption={activeSchools === null ? undefined : `${activeSchools.toLocaleString()} active`}
          icon={<ChildrenIcon size={18} />}
          tone="primary"
        />
        <StatCard
          label="Enrolled seats"
          value={countText(totalEnrolment)}
          caption={
            unattributed === null
              ? undefined
              : unattributed === 0
                ? 'Every child is in a class'
                : `${unattributed.toLocaleString()} in no class`
          }
          icon={<ReportsIcon size={18} />}
          tone="teal"
        />
        <StatCard
          label="Active teachers"
          value={countText(activeTeachers)}
          icon={<TargetIcon size={18} />}
          tone="leaf"
        />
        <StatCard
          label="Operator accounts"
          value={countText(totalOps)}
          caption={activeOps === null ? undefined : `${activeOps.toLocaleString()} active`}
          icon={<PrivacyIcon size={18} />}
          tone="indigo"
        />
      </StatGrid>

      {/* ── Main Middle Layout: 2 Columns ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ── Left Column (7 cols) ── */}
        <div className="lg:col-span-7 space-y-6">
          {/* Card 1: Is it working */}
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-canvas text-ink">
                  <TargetIcon size={16} />
                </span>
                <h3 className="text-dash-md font-extrabold text-ink">{t.healthTitle}</h3>
              </div>
              <Select
                aria-label="Filter window"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="h-8 text-dash-xs py-1"
              >
                <option value={7}>{t.healthWindow(7)}</option>
                <option value={14}>{t.healthWindow(14)}</option>
                <option value={30}>{t.healthWindow(30)}</option>
                <option value={90}>{t.healthWindow(90)}</option>
              </Select>
            </div>

            <p className="text-dash-xs text-muted mb-4 max-w-[60ch]">{t.healthSubtitle}</p>

            <Surface resource={health} lang={lang} skeletonRows={3}>
              {(h) => (
                <div className="space-y-4">
                  {/*
                    ⚠️ THE WINDOW IS THE SERVER'S, NOT THE SELECT'S. The dropdown
                    asks; `windowDays` answers. Printing the request beside a
                    figure computed from something else is how a sentence starts
                    describing a period the query did not use.
                  */}
                  <p className="text-dash-xs text-muted">{t.healthWindow(h.windowDays)}</p>

                  {/* Tracking — a pair and a verdict, never a bare rate. */}
                  <div className="rounded-xl border border-line p-3.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-bold text-ink">{t.healthTracking}</p>
                      <StatusPill
                        tone={trackingTone(h.tracking.overall.verdict)}
                        icon={toneIcon(trackingTone(h.tracking.overall.verdict))}
                      >
                        {trackingVerdictText(h.tracking.overall.verdict, lang)}
                      </StatusPill>
                    </div>
                    <p className="text-dash-xs text-muted mt-1">
                      {t.healthTrackingPair(
                        h.tracking.overall.abandonedTracking,
                        h.tracking.overall.started,
                      )}
                    </p>
                  </div>

                  {/* Frame rate — the slowest tenth, or an admission of thin data. */}
                  <div className="rounded-xl border border-line p-3.5">
                    <p className="font-bold text-ink">{t.healthFps}</p>
                    <p className="text-dash-xs text-muted mt-1">
                      {h.fps.p10 === null || h.fps.median === null
                        ? t.healthFpsThin
                        : t.healthFpsPair(h.fps.p10, h.fps.median, h.fps.samples)}
                    </p>
                    {h.fps.belowFloor && (
                      <Note tone="warning" icon={<AlertIcon size={16} />}>{t.healthBelowFloor}</Note>
                    )}
                  </div>

                  {/* The counter-metric. */}
                  <div className="rounded-xl border border-line p-3.5">
                    <p className="font-bold text-ink">{t.healthFallback}</p>
                    <p className="text-dash-xs text-muted mt-1">
                      {t.healthFallbackPair(h.fallback.usedFallback, h.fallback.completed)}
                    </p>
                    <p className="text-dash-xs text-muted mt-1.5">{t.healthFallbackWhy}</p>
                  </div>

                  {/* Where to look first. */}
                  <div className="rounded-xl border border-line p-3.5">
                    <p className="font-bold text-ink">{t.healthWorseThan}</p>
                    {h.tracking.worseThanOverall.length === 0 ? (
                      <p className="text-dash-xs text-muted mt-1">{t.healthNoSegments}</p>
                    ) : (
                      <ul className="mt-2 flex flex-wrap gap-1.5">
                        {h.tracking.worseThanOverall.map((segment) => (
                          <li
                            key={segment}
                            className="rounded-full bg-canvas px-2.5 py-0.5 font-mono text-dash-xs text-ink"
                          >
                            {segment}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </Surface>
          </div>

          {/* Card 2: Switches */}
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-canvas text-ink">
                  <AlertIcon size={16} />
                </span>
                <h3 className="text-dash-md font-extrabold text-ink">{t.systemSwitchesSection}</h3>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={currentMaint?.enabled ?? false}
                aria-label={t.maintenanceToggle}
                disabled={maintBusy || currentMaint === null}
                onClick={() => void writeMaintenance(!currentMaint?.enabled, maintText)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                  currentMaint?.enabled ? 'bg-primary' : 'bg-line-strong'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    currentMaint?.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="mt-3">
              <p className="font-bold text-ink">{t.maintenanceTitle}</p>
              {/* 🔴 The state in words. The toggle alone is a position, not a fact. */}
              {currentMaint !== null && (
                <p className="text-dash-xs font-bold text-ink mt-1">
                  {currentMaint.enabled ? t.maintenanceOn : t.maintenanceOff}
                </p>
              )}
              <p className="text-dash-xs text-muted mt-1">{t.maintenanceMeaning}</p>

              <div className="mt-4 space-y-2">
                <Field label={t.maintenanceMessage} width="full">
                  <Input
                    placeholder={t.maintenanceMessagePlaceholder}
                    value={maintText}
                    onChange={(e) => setMaintEdit(e.target.value)}
                  />
                </Field>
                {maintDirty && (
                  <DashButton
                    variant="secondary"
                    disabled={maintBusy}
                    onClick={() =>
                      void writeMaintenance(currentMaint?.enabled ?? false, maintText)
                    }
                  >
                    {maintBusy ? t.maintenanceSaving : t.maintenanceSave}
                  </DashButton>
                )}
                {currentMaint?.updatedAt && (
                  <p className="text-dash-xs text-muted">
                    {t.maintenanceSince(new Date(currentMaint.updatedAt).toLocaleString())}
                  </p>
                )}
                {maintFailed && <Note tone="danger" icon={<AlertIcon size={16} />}>{t.maintenanceFailed}</Note>}
              </div>
            </div>
          </div>

          {/* Card 3: Jobs */}
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
            <div className="flex items-center gap-2 mb-1">
              <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-canvas text-ink">
                <ReportsIcon size={16} />
              </span>
              <h3 className="text-dash-md font-extrabold text-ink">{t.jobsTitle}</h3>
            </div>
            <p className="text-dash-xs text-muted mb-4">{t.jobsSubtitle}</p>

            <div className="space-y-3">
              {JOB_NAMES.map((name) => {
                const result = jobResults[name]
                const destructive = name === 'data-retention'
                return (
                  <div
                    key={name}
                    className="flex flex-col gap-1 rounded-xl border border-line p-3.5"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-bold text-ink text-dash-sm">{t.jobLabel[name]}</p>
                        <p className="text-dash-xs text-muted">{t.jobWhat[name]}</p>
                        {/* ⚠️ The slug stays, small, beside the name — it is what
                            the route takes and what a log line says. */}
                        <span className="font-mono text-dash-xs text-muted bg-canvas px-1.5 py-0.5 rounded mt-0.5 inline-block">
                          {name}
                        </span>
                      </div>
                      <DashButton
                        variant={destructive ? 'destructive' : 'secondary'}
                        disabled={jobBusy !== null}
                        onClick={() =>
                          destructive ? setConfirmingJob(name) : void handleRunJob(name)
                        }
                      >
                        {jobBusy === name ? t.jobRunning : t.jobRun}
                      </DashButton>
                    </div>

                    {result && (
                      <div className="mt-1 space-y-1">
                        <p className="text-dash-xs font-bold text-ink">
                          {t.jobResult(result.processed, result.failed, result.durationMs)}
                        </p>
                        {/*
                          🔴 WHICH MODE IT REALLY RAN IN, AFTER THE FACT.
                          `RETENTION_DRY_RUN` is in the environment and not on
                          this screen, so the run itself has to say.
                        */}
                        {destructive &&
                          (result.dryRun ? (
                            <p className="text-dash-xs font-bold text-ink">{t.jobDryRun}</p>
                          ) : (
                            <Note tone="danger" icon={<AlertIcon size={16} />}>
                              {t.jobLive}
                            </Note>
                          ))}
                      </div>
                    )}

                    {jobFailed === name && <Note tone="danger" icon={<AlertIcon size={16} />}>{t.jobFailed}</Note>}

                    {confirmingJob === name && (
                      <div className="mt-2 rounded-xl border border-line p-3">
                        <p className="text-dash-xs font-bold text-ink">
                          {t.jobConfirmRetention}
                        </p>
                        <div className="flex gap-2 mt-3">
                          <DashButton
                            variant="destructive"
                            disabled={jobBusy !== null}
                            onClick={() => void handleRunJob(name)}
                          >
                            {t.jobConfirm}
                          </DashButton>
                          <DashButton
                            variant="secondary"
                            onClick={() => setConfirmingJob(null)}
                          >
                            {t.cancel}
                          </DashButton>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* ── Right Column (5 cols) ── */}
        <div className="lg:col-span-5 space-y-6">
          {/* Card 1: System overview */}
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-canvas text-ink">
                  <InfoIcon size={16} />
                </span>
                <h3 className="text-dash-md font-extrabold text-ink">System overview</h3>
              </div>
            </div>

            <Surface resource={overview} lang={lang} skeletonRows={4}>
              {() => (
                <>
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    {/* Donut — arcs are the real shares, or nothing at all. */}
                    <div className="relative w-36 h-36 shrink-0 flex items-center justify-center">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36" aria-hidden="true">
                        <path d={DONUT} fill="none" stroke="var(--color-line)" strokeWidth="3.8" />
                        {SCHOOL_SLICES.map((slice, i) => {
                          const pct = [activePct, onboardingPct, suspendedPct, archivedPct][i]
                          const before = [activePct, onboardingPct, suspendedPct, archivedPct]
                            .slice(0, i)
                            .reduce<number>((n, p) => n + (p ?? 0), 0)
                          if (pct === null || pct === 0) return null
                          return (
                            <path
                              key={slice.key}
                              d={DONUT}
                              fill="none"
                              stroke={slice.stroke}
                              strokeWidth="4"
                              strokeDasharray={`${pct}, 100`}
                              strokeDashoffset={`-${before}`}
                            />
                          )
                        })}
                      </svg>
                      <div className="absolute flex flex-col items-center justify-center text-center">
                        <span className="text-dash-xs text-muted">Schools</span>
                        <span className="text-dash-sm font-extrabold text-ink">
                          {countText(totalSchools)}
                        </span>
                      </div>
                    </div>

                    {/* Legend — a count first, the share only if it is knowable. */}
                    <div className="space-y-1.5 text-dash-xs w-full">
                      {SCHOOL_SLICES.map((slice, i) => {
                        const n = [activeSchools, onboardingSchools, suspendedSchools, archivedSchools][i]
                        const pct = [activePct, onboardingPct, suspendedPct, archivedPct][i]
                        return (
                          <div key={slice.key} className="flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full"
                                style={{ background: slice.stroke }}
                              />
                              <span>{slice.label}</span>
                            </span>
                            <span className="font-bold text-ink">
                              {countText(n)}
                              {pct === null ? '' : ` (${pct}%)`}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-line flex items-center justify-between text-dash-xs text-muted">
                    <span>
                      {overview.data?.generatedAt
                        ? t.maintenanceSince(
                            new Date(overview.data.generatedAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            }),
                          )
                        : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => overview.retry()}
                      className="hover:text-ink font-bold transition-colors"
                    >
                      {t.retry}
                    </button>
                  </div>
                </>
              )}
            </Surface>
          </div>

          {/* Card 2: Recent activity */}
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
            <h3 className="text-dash-md font-extrabold text-ink mb-3">Recent activity</h3>

            <div className="space-y-3">
              {recentAudit.data?.rows && recentAudit.data.rows.length > 0 ? (
                recentAudit.data.rows.map((row) => (
                  <div key={row.id} className="flex items-start justify-between text-dash-xs">
                    <div className="flex items-start gap-2.5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-canvas text-ink mt-0.5 shrink-0">
                        <CheckIcon size={14} />
                      </span>
                      <div className="min-w-0">
                        <p className="font-bold text-ink">
                          {t.auditActionLabel[row.action] ?? row.action}
                        </p>
                        <p className="text-muted font-mono break-all">
                          {auditActor(row, t.system).name} &rarr;{' '}
                          {auditTarget(row).label ?? auditTarget(row).type}
                        </p>
                      </div>
                    </div>
                    <span className="text-muted whitespace-nowrap">
                      {new Date(row.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-dash-xs text-muted py-6 text-center">
                  No recent platform activity recorded.
                </p>
              )}
            </div>

            {/* ⚠️ `#/platform/audit` — the route this tier actually has. `#/audit`
                matched nothing and fell through to the landing screen. */}
            <div className="mt-4 pt-3 border-t border-line text-right">
              <a
                href="#/platform/audit"
                className="text-dash-xs font-bold text-primary hover:underline inline-flex items-center gap-1"
              >
                View all activity &rarr;
              </a>
            </div>
          </div>

          {/* Card 3: Feature flags */}
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-card">
            <div className="flex items-center gap-2 mb-1">
              <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-canvas text-ink">
                <StickerIcon size={16} />
              </span>
              <h3 className="text-dash-md font-extrabold text-ink">{t.flagsTitle}</h3>
            </div>
            <p className="text-dash-xs text-muted mb-4">{t.flagsSubtitle}</p>

            <Field label={t.flagsPickSchool} width="full">
              <Select
                value={selectedSchoolId}
                onChange={(e) => setSelectedSchoolId(e.target.value)}
              >
                <option value="">{t.flagsPickSchoolNone}</option>
                {(schools.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>

            {selectedSchoolId && (
              <div className="mt-4 pt-3 border-t border-line space-y-3">
                {flags.data && flags.data.length === 0 ? (
                  <p className="text-dash-xs text-muted">{t.flagsEmpty}</p>
                ) : (
                  (flags.data ?? []).map((f) => (
                    <div key={f.feature} className="flex items-center justify-between gap-2 text-dash-xs">
                      <div className="min-w-0">
                        <span className="font-bold text-ink font-mono break-all">{f.feature}</span>
                        {f.note && <p className="text-muted">{f.note}</p>}
                      </div>
                      <DashButton
                        variant="secondary"
                        disabled={flagBusy}
                        onClick={() => void handleSetFlag(f.feature, !f.enabled, f.note ?? '')}
                      >
                        {f.enabled ? t.flagOn : t.flagOff}
                      </DashButton>
                    </div>
                  ))
                )}

                <p className="text-dash-xs text-muted">{t.flagBetaHint}</p>

                {/* Set a flag */}
                <div className="pt-3 border-t border-line space-y-2">
                  <p className="text-dash-xs font-bold text-ink">{t.flagAdd}</p>
                  <Field label={t.flagAddName} width="full">
                    <Input
                      placeholder={BETA_CONTENT_FEATURE}
                      value={flagName}
                      onChange={(e) => setFlagName(e.target.value)}
                    />
                  </Field>
                  <Field label={t.flagAddNote} width="full">
                    <Input
                      value={flagNote}
                      onChange={(e) => setFlagNote(e.target.value)}
                    />
                  </Field>
                  <DashButton
                    disabled={flagBusy || !flagName.trim()}
                    onClick={() => {
                      void handleSetFlag(flagName.trim().toLowerCase(), true, flagNote)
                      setFlagName('')
                      setFlagNote('')
                    }}
                  >
                    {t.set}
                  </DashButton>
                </div>

                {flagFailed && <Note tone="danger" icon={<AlertIcon size={16} />}>{t.flagFailed}</Note>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer Banner ── */}
      <Note tone="info" icon={<InfoIcon size={16} />}>
        Jobs run in the background. You can leave this page.
      </Note>
    </section>
  )
}
